import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";

import { useT } from "../helpers/useT";
// @ts-ignore
import { chauffeurDetail } from "../routes";
import { Checkbox } from "../components/fields/Checkbox";
import linkStyles from "../components/DetailsLink.module.css";
import styles from "./ChauffeurInvitedModal.module.css";
import closeModalIcon from "../../static/close-icon.svg";
import { dismissChauffeurInviteNotice } from "./chauffeurInviteNotice";

interface ChauffeurInvitedModalProps {
  uuid: string;
  firstName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ChauffeurInvitedModal({
  uuid,
  firstName,
  isOpen,
  onClose,
}: ChauffeurInvitedModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);
  const t = useT();

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const close = useCallback(() => {
    if (dontShowAgain) {
      dismissChauffeurInviteNotice();
    }
    onClose();
  }, [dontShowAgain, onClose]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      dialogRef.current?.focus();
      document.body.classList.add("dialog-open");
    }

    return () => {
      document.body.classList.remove("dialog-open");
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      close();
    }
  };

  if (!isOpen) {
    return null;
  }

  // Defensive check for SSR safety
  if (typeof document === "undefined") {
    return null;
  }

  const detailLink = chauffeurDetail({ qs: { id: uuid } });

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chauffeur-invited-dialog-title"
        className={styles.dialog}
        tabIndex={-1}
        data-qa="chauffeur-invited-modal"
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={close}
          aria-label={t("shared.close")}
        >
          <img src={closeModalIcon} alt="" aria-hidden="true" />
        </button>

        <h2 id="chauffeur-invited-dialog-title" className={styles.title}>
          {t("chauffeurs.invite.modal.title")}
        </h2>

        <p className={styles.body}>{t("chauffeurs.invite.modal.body")}</p>

        <p className={styles.uploadYourself}>
          {t("chauffeurs.invite.modal.upload-yourself")}{" "}
          <Link {...detailLink}>
            <a className={linkStyles.inlined} data-qa="chauffeur-detail-link">
              {firstName
                ? t("chauffeurs.invite.modal.details-link", { firstName })
                : t("chauffeurs.invite.modal.details-link-generic")}
            </a>
          </Link>
        </p>

        <div className={styles.footer}>
          <Checkbox
            label={t("chauffeurs.invite.modal.dont-show-again")}
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <button type="button" onClick={close} data-qa="modal-confirm">
            {t("chauffeurs.invite.modal.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
